import { useEffect, useRef } from "react";
import { cn } from "@project/lib/utils";

export type FormStep = {
  label: string;
  shortLabel?: string;
};

export type FormStepperProps = {
  steps: FormStep[];
  currentStep: number;
  className?: string;
};

export function FormStepper({
  steps,
  currentStep,
  className,
}: FormStepperProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const el = stepRefs.current[currentStep];
    if (!el || !scrollRef.current) {
      return;
    }
    el.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [currentStep]);

  return (
    <div className={cn(className)}>
      <div
        ref={scrollRef}
        className="overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar]:h-1.5"
      >
        <div className="flex w-max min-w-full items-center justify-start gap-0.5 px-1 sm:justify-center sm:gap-1 md:gap-1.5">
          {steps.map((step, i) => {
            const done = i < currentStep;
            const current = i === currentStep;
            const upcoming = i > currentStep;

            return (
              <div
                key={step.label}
                ref={el => {
                  stepRefs.current[i] = el;
                }}
                className="flex shrink-0 items-center gap-0.5 sm:gap-1 md:gap-1.5"
              >
                <div
                  className={cn(
                    "flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] sm:gap-1.5 sm:px-2.5 sm:py-1 sm:text-xs md:px-3 md:py-1.5 md:text-sm",
                    current &&
                      "bg-gold/10 ring-gold/30 border-gold text-primary shadow-sm ring-1",
                    done && "border-primary/30 bg-primary/5 text-primary",
                    upcoming && "border-border text-muted-foreground"
                  )}
                >
                  <span
                    className={cn(
                      "grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10px] font-bold sm:h-6 sm:w-6 sm:text-[11px] md:h-7 md:w-7 md:text-xs",
                      current && "bg-gold text-gold-foreground",
                      done && "bg-primary text-primary-foreground",
                      upcoming && "bg-muted text-muted-foreground"
                    )}
                  >
                    {done ? "✓" : i + 1}
                  </span>
                  <span className="max-w-[4.25rem] truncate font-semibold sm:max-w-[5.5rem] md:max-w-none">
                    {step.shortLabel ?? step.label}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div
                    className={cn(
                      "h-0.5 w-2 shrink-0 sm:w-4 md:w-8",
                      i < currentStep ? "bg-gold" : "bg-border"
                    )}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* <div className="text-center">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-primary">
          Step {currentStep + 1} of {steps.length}
        </p>
        <p className="mt-1 font-serif text-lg font-bold text-foreground sm:text-xl">{active?.label}</p>
      </div> */}
    </div>
  );
}
